/**
 * Nomination Role Permissions Utility
 * Implements role inheritance: Employee < Manager < Reviewer < Admin
 * Each higher role has all capabilities of lower roles plus their own.
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6
 */

import { NominationRole } from '../types/nomination';
import { UserRole } from '../types/user';
import { getNominationSettings, NominationSettings } from '../services/nominationSettings';
import { sameIdentity } from '../services/identity';

/**
 * Role hierarchy levels (higher number = more permissions)
 * Requirements: 1.1
 */
export const NOMINATION_ROLE_HIERARCHY: Record<NominationRole, number> = {
  [NominationRole.EMPLOYEE]: 1,
  [NominationRole.MANAGER]: 2,
  [NominationRole.REVIEWER]: 3,
  [NominationRole.ADMIN]: 4,
};

/**
 * Permission types for the Award Nomination System
 */
export enum NominationPermission {
  // Employee permissions
  VIEW_FEED = 'VIEW_FEED',

  // Manager permissions (includes Employee)
  CREATE_NOMINATION = 'CREATE_NOMINATION',
  VIEW_OWN_NOMINATIONS = 'VIEW_OWN_NOMINATIONS',
  EDIT_OWN_DRAFT = 'EDIT_OWN_DRAFT',
  SUBMIT_NOMINATION = 'SUBMIT_NOMINATION',

  // Reviewer permissions (includes Manager)
  VIEW_REVIEW_QUEUE = 'VIEW_REVIEW_QUEUE',
  ADD_NOTES = 'ADD_NOTES',
  APPROVE_NOMINATION = 'APPROVE_NOMINATION',
  REJECT_NOMINATION = 'REJECT_NOMINATION',
  PUBLISH_NOMINATION = 'PUBLISH_NOMINATION',

  // Admin permissions (includes Reviewer)
  MANAGE_ALL_NOMINATIONS = 'MANAGE_ALL_NOMINATIONS',
  DELETE_NOMINATION = 'DELETE_NOMINATION',
  EDIT_ANY_NOMINATION = 'EDIT_ANY_NOMINATION',
  VIEW_AUDIT_LOG = 'VIEW_AUDIT_LOG',

  // Voting permissions (Reviewer+)
  CREATE_VOTING_POLL = 'CREATE_VOTING_POLL',
  VIEW_VOTING_RESULTS = 'VIEW_VOTING_RESULTS',
  MANAGE_VOTING_POLLS = 'MANAGE_VOTING_POLLS',
}

/**
 * Permissions granted at each role level (not cumulative — inheritance is computed).
 */
const ROLE_PERMISSIONS: Record<NominationRole, NominationPermission[]> = {
  [NominationRole.EMPLOYEE]: [
    NominationPermission.VIEW_FEED,
    /*
     * Nominating, and seeing what you nominated, are Employee-level.
     *
     * These four sat under MANAGER while canCreateNomination() below returned true from
     * EMPLOYEE up — and canCreateNomination is what the route guard, the Home page quick
     * actions and Global Search all actually call. So an employee was shown a "Nominate
     * someone" button, was allowed through /nominations/new and /nominations/my, and had
     * no sidebar entry for either, because getNominationMenuItems read this table instead.
     * The table was the outlier; it now matches the behaviour the rest of the module ships.
     */
    NominationPermission.CREATE_NOMINATION,
    NominationPermission.VIEW_OWN_NOMINATIONS,
    NominationPermission.EDIT_OWN_DRAFT,
    NominationPermission.SUBMIT_NOMINATION,
  ],
  // Nothing above Employee yet. A Manager's extra reach over their reports is enforced by
  // the nomination data, not by a distinct permission — the level is kept so the hierarchy
  // and the Reviewer/Admin tiers stacked on top of it stay intact.
  [NominationRole.MANAGER]: [],
  [NominationRole.REVIEWER]: [
    NominationPermission.VIEW_REVIEW_QUEUE,
    NominationPermission.ADD_NOTES,
    NominationPermission.APPROVE_NOMINATION,
    NominationPermission.REJECT_NOMINATION,
    NominationPermission.PUBLISH_NOMINATION,
    NominationPermission.CREATE_VOTING_POLL,
    NominationPermission.VIEW_VOTING_RESULTS,
  ],
  [NominationRole.ADMIN]: [
    NominationPermission.MANAGE_ALL_NOMINATIONS,
    NominationPermission.DELETE_NOMINATION,
    NominationPermission.EDIT_ANY_NOMINATION,
    NominationPermission.VIEW_AUDIT_LOG,
    NominationPermission.MANAGE_VOTING_POLLS,
  ],
};


/**
 * Get the hierarchy level of a nomination role.
 */
export const getNominationRoleLevel = (role: NominationRole): number => {
  return NOMINATION_ROLE_HIERARCHY[role] || 0;
};

/**
 * Check if role1 is higher or equal to role2 in the hierarchy.
 */
export const isNominationRoleHigherOrEqual = (
  role1: NominationRole,
  role2: NominationRole
): boolean => {
  return getNominationRoleLevel(role1) >= getNominationRoleLevel(role2);
};

/**
 * Get all permissions for a nomination role (including inherited from lower roles).
 * Requirements: 1.2
 */
export const getNominationPermissions = (role: NominationRole): NominationPermission[] => {
  const permissions: NominationPermission[] = [];
  const roleLevel = getNominationRoleLevel(role);

  Object.entries(NOMINATION_ROLE_HIERARCHY).forEach(([r, level]) => {
    if (level <= roleLevel) {
      permissions.push(...ROLE_PERMISSIONS[r as NominationRole]);
    }
  });

  return [...new Set(permissions)];
};

/**
 * Check if a nomination role has a specific permission.
 */
export const hasNominationPermission = (
  role: NominationRole,
  permission: NominationPermission
): boolean => {
  return getNominationPermissions(role).includes(permission);
};

/**
 * Check if the role can create nominations (Employee+).
 * Requirements: 1.4
 */
export const canCreateNomination = (role: NominationRole): boolean => {
  return isNominationRoleHigherOrEqual(role, NominationRole.EMPLOYEE);
};

/**
 * Check if the role can review nominations (Reviewer+).
 * Requirements: 1.5
 */
export const canReviewNomination = (role: NominationRole): boolean => {
  return isNominationRoleHigherOrEqual(role, NominationRole.REVIEWER);
};

/**
 * Check if the role can publish nominations (Reviewer+).
 * Requirements: 1.5
 */
export const canPublishNomination = (role: NominationRole): boolean => {
  return isNominationRoleHigherOrEqual(role, NominationRole.REVIEWER);
};

/**
 * Check if the role can manage all nominations (Admin only).
 * Requirements: 1.6
 */
export const canManageAllNominations = (role: NominationRole): boolean => {
  return isNominationRoleHigherOrEqual(role, NominationRole.ADMIN);
};

/**
 * Check if the role can create voting polls (Reviewer+).
 */
export const canCreateVotingPoll = (role: NominationRole): boolean => {
  return isNominationRoleHigherOrEqual(role, NominationRole.REVIEWER);
};


/**
 * Map an EMS UserRole to the corresponding NominationRole.
 * EMPLOYEE -> EMPLOYEE, MANAGER -> MANAGER, FINANCE_ADMIN -> REVIEWER, SUPER_ADMIN -> ADMIN.
 *
 * Both people the Super Admin names in Nomination Settings — the receiver and the cc — are
 * treated as nomination admin, which is what puts Review Queue and Manage Nominations in
 * their sidebar. Naming someone there is the act of delegating nomination handling to them;
 * a cc who is expected to act on what they are copied on but cannot open the queue is just
 * a notification with no way to respond.
 *
 * This is a privilege grant driven by a settings field, so it is worth being explicit about
 * the blast radius: nomination Admin can approve, reject, publish, edit and delete ANY
 * nomination, and it is granted regardless of the person's EMS role — an Employee named as
 * cc becomes a nomination admin. Only Super Admin can reach Nomination Settings, so the
 * grant is always deliberate, but it is not reduced by the recipient's system role.
 *
 * Requirements: 1.1, 1.2
 */
export const mapUserRoleToNominationRole = (
  role: UserRole,
  email?: string,
  loadedSettings?: NominationSettings
): NominationRole => {
  const settings = loadedSettings || getNominationSettings();
  const receiverEmail = settings.receiverEmail.trim().toLowerCase();
  const ccEmail = settings.ccEmail.trim().toLowerCase();
  const userEmail = email?.trim().toLowerCase() || '';
  let nominationRole: NominationRole;
  let reason: string;

  /*
   * sameIdentity, not `===`.
   *
   * Every employee has one account per email domain (creyentetech.com and
   * creyenteinfotech.com — see services/identity). HR types one of those two addresses
   * into Nomination Settings, and the receiver signs in holding the other, most often
   * because Azure AD federates them on the new domain while the settings field was filled
   * in with the old one. Raw string equality then fails, this falls through to the
   * switch below, and the receiver is handed their plain system role — no Review Queue,
   * no Manage Nominations, and nothing on screen explaining why the setting did nothing.
   */
  const matchesReceiver = Boolean(userEmail && receiverEmail && sameIdentity(userEmail, receiverEmail));
  const matchesCc = Boolean(userEmail && ccEmail && sameIdentity(userEmail, ccEmail));

  if (matchesReceiver || matchesCc) {
    nominationRole = NominationRole.ADMIN;
    reason = matchesReceiver
      ? 'authenticated user email matches configured receiverEmail'
      : 'authenticated user email matches configured ccEmail';
  } else {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        nominationRole = NominationRole.ADMIN;
        reason = 'system role SUPER_ADMIN maps to nomination admin';
        break;
      case UserRole.FINANCE_ADMIN:
        nominationRole = NominationRole.REVIEWER;
        reason = 'system role FINANCE_ADMIN maps to nomination reviewer';
        break;
      case UserRole.MANAGER:
        nominationRole = NominationRole.MANAGER;
        reason = 'system role MANAGER maps to nomination manager';
        break;
      case UserRole.EMPLOYEE:
      default:
        nominationRole = NominationRole.EMPLOYEE;
        // Reaching here means neither configured address matched — both are handled above.
        reason = 'no receiverEmail or ccEmail match and system role has employee nomination access';
        break;
    }
  }

  console.info('[NominationRole]', {
    authenticatedUserEmail: userEmail,
    receiverEmail,
    ccEmail,
    systemRole: role,
    calculatedNominationRole: nominationRole,
    reason,
  });

  return nominationRole;
};

/**
 * Get the sidebar menu items permitted for a given nomination role.
 * Requirements: 7.2
 */
export interface NominationMenuItem {
  path: string;
  label: string;
}

export const getNominationMenuItems = (role: NominationRole): NominationMenuItem[] => {
  const items: NominationMenuItem[] = [];

  /*
   * Employee+: Nominations Feed and My Nominations.
   *
   * My Nominations used to be gated at Manager+, which is why an employee could reach
   * /nominations/my — the route guard allows it, Home links to it, Global Search offers
   * it — but had no way to find it from the sidebar. See ROLE_PERMISSIONS above.
   *
   * No "New Nomination" entry: My Nominations opens CreateNomination inline in a panel
   * (see NominationList), so a second menu item pointing at a standalone copy of the same
   * form is one navigation path too many. The /nominations/new route still exists for the
   * Home page quick action and Global Search, which deep-link straight into it.
   */
  items.push({ path: '/nominations/feed', label: 'Nominations Feed' });
  items.push({ path: '/nominations/my', label: 'My Nominations' });

  // Reviewer+: Review Queue
  if (isNominationRoleHigherOrEqual(role, NominationRole.REVIEWER)) {
    items.push({ path: '/nominations/review', label: 'Review Queue' });
  }

  // Admin+: Manage Nominations
  if (isNominationRoleHigherOrEqual(role, NominationRole.ADMIN)) {
    items.push({ path: '/nominations/admin', label: 'Manage Nominations' });
  }

  return items;
};
